import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { useDebounce } from '../../../../hooks/use-debounce';
import { useDialog } from '../../../../hooks/use-dialog';
import { useFiltersParam } from './use-filters-param';
import { useQueryInputParams } from './use-query-input-param';
import { useFromDayParam } from '../../../../hooks/search-params/use-from-day-param';
import { useToDayParam } from '../../../../hooks/search-params/use-to-day-param';
import { usePageParam } from '../../../../hooks/search-params/use-page-param';
import { useSyncQueryParamToState } from '../../../../hooks/search-params/use-sync-query-param-to-state';
import { isNotNullOrUndefined, withDefault } from '@vy/utils';
import { PAYMENT_FILTERABLE_FIELDS } from '@vy/payments-types';
import { FILTER_OPERATORS } from '@vy/query-specifications-types';

export const usePaymentsFilters = () => {
  const [queryParam, setQueryParam] = useQueryInputParams();
  const [query, setQuery] = useState(queryParam ?? '');
  const debouncedQuery = useDebounce(query);
  const [filters, setFilters] = useFiltersParam();
  const [fromDay] = useFromDayParam();
  const [toDay] = useToDayParam();
  const [, setPage] = usePageParam();
  const prevDebouncedQueryRef = useRef<string>(debouncedQuery);

  const {
    isDialogOpen: isFilterPaymentsOpen,
    handleOpen: openFilterPayments,
    handleClose: closeFilterPayments,
  } = useDialog();

  useSyncQueryParamToState(queryParam, setQuery);

  useEffect(() => {
    if (prevDebouncedQueryRef.current !== debouncedQuery) {
      prevDebouncedQueryRef.current = debouncedQuery;
      setQueryParam(debouncedQuery || undefined);
    }
  }, [debouncedQuery]);

  const dateFilters = useMemo(() => {
    const dateFilterLines: FilterLineType[] = [];

    if (isNotNullOrUndefined(fromDay)) {
      dateFilterLines.push({
        id: 'date-filter-from',
        field: PAYMENT_FILTERABLE_FIELDS.PAYMENT_DATE,
        operator: FILTER_OPERATORS.GTE,
        value: fromDay.startOf('day').utc(true).toISOString(),
      });
    }

    if (isNotNullOrUndefined(toDay)) {
      dateFilterLines.push({
        id: 'date-filter-to',
        field: PAYMENT_FILTERABLE_FIELDS.PAYMENT_DATE,
        operator: FILTER_OPERATORS.LTE,
        value: toDay.endOf('day').utc(true).toISOString(),
      });
    }

    return dateFilterLines;
  }, [fromDay, toDay]);

  const allFiltersForApi = useMemo(() => {
    return [...withDefault(filters, []), ...dateFilters];
  }, [filters, dateFilters]);

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);

  const handleFilterClick = useCallback((filterLines: FilterLineType[]) => {
    setFilters(filterLines);
    setPage(0);
  }, []);

  return {
    query,
    queryParam: debouncedQuery,
    handleSearchInputChange,

    filters: withDefault(filters, []),
    handleFilterClick,
    allFiltersForApi,

    isFilterPaymentsOpen,
    openFilterPayments,
    closeFilterPayments,
  };
};
